import React, { useEffect, type JSX } from 'react';
import { Stack, Box, Checkbox, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { CustomTransComponent, Hierarchy, renderGroupHierarchyIcons } from '../../../components';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { useLazyGetAllLoadsQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import { useSearchDeviceOrGroup } from '@fiji/common/src/hooks';
import { useTranslation } from 'react-i18next';
import { InputWithCustomPlaceholder } from '@fiji/web/src/components/InputWithCustomPlaceholder';
import { useGetMultiDeviceQuery } from '@fiji/common/src/features/loadGroups/loadGroupsApi';

type LoadGroupsListHierarchyProps = {
    selectedLoadIds: Array<string | { id: string }>;
    onToggleLoad: (id: string, load?: any) => void;
    selectionType?: 'checkbox' | 'radio';
    ignoreDefaultAccess?: boolean;
    allowedModelCategory?: string;
    existingLoadIds?: string[];
};

export const LoadGroupsListHierarchy = React.memo(
    ({
        selectedLoadIds,
        onToggleLoad,
        ignoreDefaultAccess = false,
        allowedModelCategory,
    }: LoadGroupsListHierarchyProps): JSX.Element => {
        const currentOrg = useTypedSelector(selectedOrg);
        const [searchKey, setSearchKey] = React.useState<any>(undefined);
        const [groupsData, setGroupsData] = React.useState<any>();
        const [hierarchyMappingData, setHierarchyMappingData] = React.useState<any>({});
        const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState<string[]>([]);
        const { currentData: filteredRecords, isLoading: searchLoader } = useSearchDeviceOrGroup({
            searchKey: searchKey,
        });
        const { t } = useTranslation();
        const { data: multiDeviceData } = useGetMultiDeviceQuery({
            params: { category: 'CATEGORY', assetCategory: 'LOAD_GROUPS' },
        });
        // API Hooks
        const [getAllGroups, { data: groupsHierarchy }] = useGetAllGroupsMutation();
        const [getLoads] = useLazyGetAllLoadsQuery();

        useEffect(() => {
            if (!groupsData && groupsHierarchy?.data?.records) {
                const records = groupsHierarchy.data.records.map((r: any) => ({
                    ...r,
                    type: r.type || 'GROUP',
                }));
                setGroupsData(records);
            }
        }, [groupsHierarchy]);

        React.useEffect(() => {
            if (currentOrg?.id) {
                void getAllGroups({});
            }
        }, [currentOrg]);

        const normalizeCategory = (value: string | undefined | null): string => value?.trim().toLowerCase() ?? '';

        const isAllowedDevice = (node: any): boolean => {
            const requiredCategory = normalizeCategory(allowedModelCategory);

            if (!requiredCategory || node?.type !== 'DEVICE') {
                return true;
            }

            return [node?.modelCategory, node?.deviceTypeCategory, node?.vendorTypeName, node?.deviceTypeName]
                .map((value) => normalizeCategory(value))
                .some((value) => value === requiredCategory);
        };

        const handleFetchHierarchy = async (parentData: any): Promise<void> => {
            setLoadingHierarchyNode((prevState) => [...prevState, parentData?.id]);

            try {
                if (parentData.type === 'DEVICE') {
                    if (!isAllowedDevice(parentData)) {
                        setHierarchyMappingData((prevState: any) => ({
                            ...prevState,
                            [parentData?.id]: [],
                        }));
                        return;
                    }

                    const { data: loadData } = await getLoads({
                        deviceId: parentData.id,
                        params: { page: 0, size: 100 },
                    });

                    const loads =
                        loadData?.data?.loadConfiguration?.map((load: any) => ({
                            ...load,
                            deviceId: parentData.id,
                            deviceTypeId: parentData.deviceTypeId,
                            name: load.loadName,
                            type: 'LOAD',
                            icon: load.icon || { web: { name: 'Light', family: 'material' } },
                        })) ?? [];

                    if (loads.length === 0) {
                        loads.push({
                            id: `no-load-${parentData.id}`,
                            name: 'No Controllable Loads',
                            type: 'NO_LOADS',
                            sx: { color: 'text.primary', fontSize: '14px' },
                        });
                    }

                    setHierarchyMappingData((prevState: any) => ({
                        ...prevState,
                        [parentData?.id]: loads,
                    }));
                } else {
                    setLoadingHierarchyNode((prevState): any => [...prevState, parentData?.id]);
                    const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
                    if (childHierarchyData) {
                        setHierarchyMappingData((prevState: any) => ({
                            ...prevState,
                            [parentData?.id]: childHierarchyData?.data?.records,
                        }));
                    }
                    setLoadingHierarchyNode((prevState): any =>
                        prevState.filter((loadingNode) => loadingNode !== parentData?.id)
                    );
                }
            } catch (error) {
                console.error('Failed to fetch hierarchy data', error);
            } finally {
                setLoadingHierarchyNode((prevState) =>
                    prevState.filter((loadingNode) => loadingNode !== parentData?.id)
                );
            }
        };

        const selectionHandler = (selectedNodeData: any): void => {
            if (selectedNodeData.type === 'LOAD') {
                const fixedLoad = {
                    ...selectedNodeData,
                    deviceId:
                        selectedNodeData.deviceId ||
                        selectedNodeData.parentId ||
                        hierarchyMappingData[selectedNodeData.parentId]?.[0]?.deviceId,
                };

                onToggleLoad(fixedLoad.id, fixedLoad);
            }
        };

        const isLoadSelected = (loadId: string): boolean =>
            selectedLoadIds?.some((item: string | { id: string }) =>
                typeof item === 'string' ? item === loadId : item?.id === loadId
            ) ?? false;

        const getLoadNumber = (data: any): JSX.Element => (
            <Box
                sx={{
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: '#727E84',
                    color: '#fff',
                    width: '18px',
                    height: '18px',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: '4px',
                }}
            >
                {parseInt(data?.['loadNumber'] ?? 0) + 1}
            </Box>
        );

        const renderCustomIcons = (treeItem: any, ...rest: any): any => {
            if (treeItem?.type === 'LOAD') {
                return getLoadNumber(treeItem);
            }
            if (treeItem?.type === 'NO_LOADS') {
                return <></>;
            }
            return renderGroupHierarchyIcons(treeItem, rest);
        };

        const filters = React.useMemo(() => {
            const listModelCat = multiDeviceData?.data?.filter((item: any) => item !== allowedModelCategory);

            if (listModelCat?.length) {
                return listModelCat.map((item: any) => ({
                    key: 'vendorTypeName',
                    operator: 'i==',
                    value: item,
                    action: 'hidden',
                }));
            }

            return [];
        }, [multiDeviceData?.data, allowedModelCategory]);

        const getGroupList = (): JSX.Element => (
            <Box display="flex" flexDirection="column">
                <Box sx={{ backgroundColor: '#f2f2f2' }}>
                    <Typography variant="h6" gutterBottom fontSize={16} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'WIDGETS:LOADS.SELECT_LOADS_WITHOUT_COUNT'} />
                    </Typography>
                    <Stack my={2}>
                        <InputWithCustomPlaceholder
                            id="searchKey"
                            value={searchKey ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                setSearchKey(e.target.value);
                            }}
                            placeholder={t('WIDGETS:BALANCING.SEARCH_GROUPS_AND_DEVICES')}
                            size="small"
                            maxWidth={'100%'}
                            InputProps={{
                                startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                            }}
                        />
                    </Stack>
                    <Box className="bg-white overflow-y-scroll" sx={{ height: 'calc(100vh - 464px)' }}>
                        <Hierarchy
                            isLoading={!groupsData || searchLoader}
                            data={
                                searchKey?.length > 0
                                    ? filteredRecords
                                    : ignoreDefaultAccess
                                    ? groupsData
                                    : currentOrg?.defaultGroupAccess
                                    ? [
                                          {
                                              ...currentOrg,
                                              name: currentOrg?.name,
                                              groupCount: currentOrg?.groupCount,
                                              deviceCount: currentOrg?.deviceCount,
                                              type: 'ORGANIZATION',
                                              icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                                          },
                                      ]
                                    : groupsData
                            }
                            hasChild={(treeItem: any): boolean =>
                                treeItem?.groupCount ||
                                treeItem?.deviceCount ||
                                (treeItem?.type === 'DEVICE' && isAllowedDevice(treeItem))
                            }
                            labelKey={'name'}
                            hierarchyMappingData={hierarchyMappingData}
                            loadingNode={loadingHierarchyNode}
                            handleTreeNodeSelection={selectionHandler}
                            getAvatarChild={renderCustomIcons}
                            autoChildSelection={false}
                            loadMore={handleFetchHierarchy}
                            loadingSkeleton={<GroupHierarchySkeleton row={1} />}
                            selectedNodes={[]}
                            selectionType={'checkbox'}
                            actions={({ treeItem, parentNode }: any) => {
                                if (treeItem?.type === 'LOAD') {
                                    const loadId = treeItem?.id || treeItem?.loadId;

                                    return (
                                        <Checkbox
                                            checked={
                                                isLoadSelected(loadId) ||
                                                (isLoadSelected(loadId) && selectedLoadIds?.length < 2)
                                            }
                                            disabled={!isLoadSelected(loadId) && selectedLoadIds?.length > 15}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                selectionHandler({
                                                    ...treeItem,
                                                    deviceName: parentNode?.name,
                                                    groupPath: parentNode?.groupPath,
                                                });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    );
                                }
                                return <></>;
                            }}
                            filters={[
                                {
                                    key: 'type',
                                    operator: '!==',
                                    value: 'LOAD',
                                    action: 'disableCheck',
                                },
                                ...filters,
                            ]}
                        />
                    </Box>
                </Box>
            </Box>
        );

        return (
            <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {getGroupList()}
            </Box>
        );
    }
);
